import React, { useState, useMemo, useEffect } from 'react';
import * as MuiIcons from '@mui/icons-material';
import {
  useAIPortfolioHoldings,
  useAIPortfolioTransactions,
  useAIPortfolioPerformance,
  useAIPortfolioMemory,
  useUpdateAIPortfolio,
  useDeleteAIPortfolio,
  useTriggerRebalance,
  fetchScreenData,
} from '../services/api';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip as RechartsTooltip, Legend } from 'recharts';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { useQueryClient } from '@tanstack/react-query';
import AiAnalysisPanel from './AiAnalysisPanel';
import AIPortfolioCreateModal from './AIPortfolioCreateModal';
import ListShareButton from './ListShareButton';
import { getListPath } from '../utils/listUrl';

const STRATEGY_LABELS = {
  momentum_short: 'Short Momentum',
  momentum_mid: 'Mid Momentum',
  momentum_long: 'Long Momentum',
  growth: 'Growth',
  value: 'Value',
  income: 'Income',
  // Legacy
  momentum: 'Momentum',
  macro_aware: 'Macro-Aware',
};

const STRATEGY_COLORS = {
  momentum_short: 'bg-red-100 text-red-700',
  momentum_mid: 'bg-purple-100 text-purple-700',
  momentum_long: 'bg-indigo-100 text-indigo-700',
  growth: 'bg-green-100 text-green-700',
  value: 'bg-blue-100 text-blue-700',
  income: 'bg-yellow-100 text-yellow-700',
  // Legacy
  momentum: 'bg-purple-100 text-purple-700',
  macro_aware: 'bg-orange-100 text-orange-700',
};

const TAX_TREATMENT_LABELS = {
  tax_agnostic: 'Tax-Agnostic',
  short_term_friendly: 'Short-Term Friendly',
  long_term_optimized: 'Long-Term Optimized',
};

const getBaseStrategy = (strategy) => strategy?.split('.')[0] || strategy;

const tradingDaysSince = (dateStr) => {
  if (!dateStr) return null;
  const start = new Date(dateStr + 'T00:00:00');
  const now = new Date();
  let count = 0;
  const d = new Date(start);
  while (d < now) {
    const day = d.getDay();
    if (day !== 0 && day !== 6) count++;
    d.setDate(d.getDate() + 1);
  }
  return count;
};

const formatCurrency = (val) => {
  if (val == null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);
};

const formatPct = (val) => {
  if (val == null) return '—';
  const sign = val > 0 ? '+' : '';
  return `${sign}${val.toFixed(2)}%`;
};

const formatDateShort = (dateStr) => {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const PerformanceChart = ({ portfolioId }) => {
  const { data, isLoading, error } = useAIPortfolioPerformance(portfolioId);

  if (isLoading) return <div className="p-8 text-center text-gray-400">Loading performance data...</div>;
  if (error) return <div className="p-8 text-center text-red-400">Failed to load performance data.</div>;
  if (!data?.dates?.length) return <div className="p-8 text-center text-gray-500">No performance data yet. Transactions are needed to generate the chart.</div>;

  const chartData = data.dates.map((date, i) => ({
    date,
    dateLabel: formatDateShort(date),
    backtest: data.backtest?.[i],
    portfolio: data.portfolio[i],
    spy: data.spy[i],
  }));

  const allValues = [
    ...(data.backtest || []).filter(Boolean),
    ...data.portfolio.filter(Boolean),
    ...data.spy.filter(Boolean),
  ];
  const rawMin = Math.min(...allValues) * 0.98;
  const rawMax = Math.max(...allValues) * 1.02;
  const step = Math.pow(10, Math.floor(Math.log10(rawMax - rawMin)) - 1) * 5;
  const minVal = Math.floor(rawMin / step) * step;
  const maxVal = Math.ceil(rawMax / step) * step;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-700">Portfolio vs {data.benchmark || 'SPY'}</h3>
        <span className="text-[10px] text-gray-400">Backtest shows current holdings applied to historical prices, not strategy simulation</span>
      </div>
      <ResponsiveContainer width="100%" height={350}>
        <LineChart data={chartData}>
          <XAxis
            dataKey="dateLabel"
            tick={{ fontSize: 11 }}
            interval="preserveStartEnd"
          />
          <YAxis
            domain={[minVal, maxVal]}
            tick={{ fontSize: 11 }}
            tickFormatter={(v) => `$${v.toLocaleString()}`}
          />
          <RechartsTooltip
            formatter={(value, name) => value != null ? [`$${value.toLocaleString()}`, name] : [null, null]}
            labelFormatter={(_, payload) => payload?.[0]?.payload?.date || ''}
          />
          <Legend />
          <Line type="monotone" dataKey="backtest" name="Portfolio (1Y backtest)" stroke="#93c5fd" strokeWidth={1.5} dot={false} />
          <Line type="monotone" dataKey="portfolio" name="Portfolio (live)" stroke="#3b82f6" strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="spy" name={data.benchmark || 'SPY'} stroke="#9ca3af" strokeWidth={2} dot={false} strokeDasharray="5 5" />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const AiDecisionsPanel = ({ summary, memoryEntries, transactions }) => {
  const [expandedCycle, setExpandedCycle] = useState(null);
  const data = summary ? (typeof summary === 'string' ? JSON.parse(summary) : summary) : null;

  const initial = data?.initial;
  const initialRationale = initial?.rationale || data?.allocationRationale;

  const hasMemory = memoryEntries && memoryEntries.length > 0;

  // Group transactions by date for showing in each cycle
  const txByDate = useMemo(() => {
    if (!transactions) return {};
    const map = {};
    for (const tx of transactions) {
      const date = tx.date;
      if (!map[date]) map[date] = [];
      map[date].push(tx);
    }
    return map;
  }, [transactions]);

  // Calculate summary stats
  const recentWinRate = hasMemory ? (() => {
    const recent = memoryEntries.slice(0, 5);
    const allOutcomes = recent.flatMap(e => e.trade_outcomes ? JSON.parse(e.trade_outcomes) : []);
    const buys = allOutcomes.filter(o => o.action === 'buy');
    if (buys.length === 0) return null;
    return Math.round((buys.filter(o => o.reasoning_was_correct).length / buys.length) * 100);
  })() : null;

  if (!initialRationale && !hasMemory) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500">
        No strategy data yet.
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      {/* Initial Strategy */}
      {initialRationale && (
        <div className="mb-4 pb-4 border-b border-gray-100">
          <div className="flex items-center gap-2 mb-2">
            <MuiIcons.Psychology style={{ fontSize: 18 }} className="text-blue-600" />
            <span className="text-sm font-semibold text-gray-800">Initial Strategy</span>
            {initial?.timestamp && (
              <span className="text-xs text-gray-400">{new Date(initial.timestamp).toLocaleDateString()}</span>
            )}
            {recentWinRate !== null && (
              <span className={`text-xs px-1.5 py-0.5 rounded ml-auto ${recentWinRate >= 50 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {recentWinRate}% accuracy
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600 leading-relaxed">{initialRationale}</p>
        </div>
      )}

      {/* Rebalancing History */}
      {hasMemory && (
        <div>
          <div className="text-sm font-semibold text-gray-800 mb-3">Rebalancing History</div>
          <div className="space-y-2">
            {memoryEntries.map((entry) => {
              const outcomes = entry.trade_outcomes ? JSON.parse(entry.trade_outcomes) : [];
              const wins = outcomes.filter(o => o.reasoning_was_correct).length;
              const buyCount = outcomes.filter(o => o.action === 'buy').length;
              const plSign = (entry.portfolio_pl_pct || 0) >= 0 ? '+' : '';
              const plColor = (entry.portfolio_pl_pct || 0) >= 0 ? 'text-green-600' : 'text-red-600';
              const isExpanded = expandedCycle === entry.id;
              const cycleTx = txByDate[entry.cycle_date] || [];

              return (
                <div key={entry.id} className="border border-gray-100 rounded-lg overflow-hidden">
                  {/* Row header */}
                  <div
                    className="px-3 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => setExpandedCycle(isExpanded ? null : entry.id)}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-sm font-mono text-gray-600">{entry.cycle_date}</span>
                      <span className={`text-sm font-semibold ${plColor}`}>{plSign}{entry.portfolio_pl_pct}%</span>
                      {entry.fear_greed && (
                        <span className="text-xs bg-gray-100 px-1.5 py-0.5 rounded text-gray-500">F&G {entry.fear_greed}</span>
                      )}
                      <div className="flex items-center gap-2 ml-auto">
                        {cycleTx.length > 0 && (
                          <span className="text-xs text-blue-600">{cycleTx.length} trade{cycleTx.length > 1 ? 's' : ''}</span>
                        )}
                        {outcomes.length > 0 && buyCount > 0 && (
                          <span className={`text-xs ${wins === buyCount ? 'text-green-600' : 'text-gray-400'}`}>
                            {wins}/{buyCount} ✓
                          </span>
                        )}
                        <MuiIcons.ExpandMore
                          style={{ fontSize: 18 }}
                          className={`text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                        />
                      </div>
                    </div>

                    {/* Rationale */}
                    {entry.rationale && (
                      <p className={`text-sm text-gray-600 leading-relaxed ${isExpanded ? '' : 'line-clamp-2'}`}>
                        {entry.rationale}
                      </p>
                    )}
                  </div>

                  {/* Expanded details */}
                  {isExpanded && (
                    <div className="px-3 pb-3 pt-2 border-t border-gray-100 bg-gray-50 space-y-3">
                      {/* Transactions this cycle */}
                      {cycleTx.length > 0 && (
                        <div className="bg-white rounded border border-gray-200 divide-y divide-gray-100">
                          {cycleTx.map((tx, i) => (
                            <div key={i} className="flex items-center gap-3 px-3 py-2 text-sm">
                              <span className={`font-semibold w-10 ${tx.action === 'buy' ? 'text-green-600' : 'text-red-600'}`}>
                                {tx.action.toUpperCase()}
                              </span>
                              <span className="font-medium text-gray-800 w-16">{tx.symbol}</span>
                              <span className="text-gray-600">{tx.shares} @ ${tx.price?.toFixed(2)}</span>
                              <span className="text-gray-500 ml-auto">${(tx.shares * tx.price).toFixed(0)}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Learning: Prior trade scores + Reflection */}
                      <div className="bg-blue-50 rounded-lg p-3">
                        <div className="text-xs font-semibold text-blue-700 mb-2 flex items-center gap-1">
                          <MuiIcons.School style={{ fontSize: 14 }} />
                          Learning
                        </div>

                        {outcomes.length > 0 ? (
                          <>
                            <div className="text-xs text-blue-600 mb-1.5">How prior trades performed:</div>
                            <div className="flex flex-wrap gap-1 mb-2">
                              {outcomes.map((o, i) => (
                                <span
                                  key={i}
                                  className={`text-xs px-1.5 py-0.5 rounded ${
                                    o.action === 'sell' ? 'bg-orange-100 text-orange-600' :
                                    o.reasoning_was_correct ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                                  }`}
                                >
                                  {o.symbol} {o.pl_pct != null ? `${o.pl_pct > 0 ? '+' : ''}${o.pl_pct}%` : ''}
                                </span>
                              ))}
                            </div>
                          </>
                        ) : (
                          <p className="text-xs text-blue-500 italic mb-2">No prior trades to evaluate this cycle.</p>
                        )}

                        {entry.reflection && entry.reflection !== 'No trades to reflect on since last cycle.' ? (
                          <div>
                            <div className="text-xs text-blue-600 mb-1">AI Reflection:</div>
                            <p className="text-sm text-blue-800 leading-relaxed italic">"{entry.reflection}"</p>
                          </div>
                        ) : (
                          <p className="text-xs text-blue-500 italic">Reflection will appear after trades are scored.</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const PortfolioView = ({ portfolioId, onBack, loggedInUser }) => {
  const [activeTab, setActiveTab] = useState('holdings');
  const [expandedTx, setExpandedTx] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [stockDataMap, setStockDataMap] = useState({});
  const [latestRebalanceSummary, setLatestRebalanceSummary] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const queryClient = useQueryClient();
  const { data: holdingsData, isLoading: holdingsLoading } = useAIPortfolioHoldings(portfolioId);
  const { data: transactions, isLoading: txLoading } = useAIPortfolioTransactions(portfolioId);
  const { data: memoryEntries } = useAIPortfolioMemory(portfolioId);
  const updateMutation = useUpdateAIPortfolio();
  const deleteMutation = useDeleteAIPortfolio();
  const rebalanceMutation = useTriggerRebalance();

  const portfolio = holdingsData?.portfolio;
  const holdings = holdingsData?.holdings || [];
  const summary = holdingsData?.summary;
  const isOwner = loggedInUser?.email && portfolio?.owner_id === loggedInUser.email;

  // Fetch full stock data for AI analysis panels
  useEffect(() => {
    if (holdings.length === 0) return;
    const symbols = holdings.map(h => h.symbol);
    fetchScreenData(null, null, symbols)
      .then(stocks => {
        const map = {};
        for (const s of stocks) {
          map[s.tickerSymbol || s.symbol] = s;
        }
        setStockDataMap(map);
      })
      .catch(err => console.error('[PORTFOLIO-VIEW] Failed to fetch stock data:', err));
  }, [holdings.length]); // re-fetch only when holdings count changes

  const handleToggleStatus = async () => {
    if (!portfolio) return;
    const newStatus = portfolio.status === 'active' ? 'paused' : 'active';
    await updateMutation.mutateAsync({ id: portfolioId, status: newStatus });
    queryClient.invalidateQueries(['aiPortfolioHoldings', portfolioId]);
  };

  const handleToggleNotifications = async () => {
    if (!portfolio) return;
    const newValue = portfolio.notifications_enabled === 0 ? 1 : 0;
    await updateMutation.mutateAsync({ id: portfolioId, notifications_enabled: newValue });
    queryClient.invalidateQueries(['aiPortfolioDetail', portfolioId]);
  };

  const handleTogglePublic = async () => {
    if (!portfolio) return;
    const newValue = portfolio.is_public ? 0 : 1;
    await updateMutation.mutateAsync({ id: portfolioId, is_public: newValue });
    // Patch the owned-portfolios list in place first (the /lists index reads it), so a cached
    // copy can't show the old flag — and re-offer a just-privatised portfolio's URL — while it
    // refetches; then re-sync with the server.
    queryClient.setQueriesData({ queryKey: ['aiPortfolios'] }, (old) => (
      Array.isArray(old) ? old.map((p) => (p.id === portfolioId ? { ...p, is_public: newValue } : p)) : old
    ));
    // Same for the holdings query this view reads is_public from (the Share button follows it), so
    // the button drops the moment a portfolio goes private rather than after a refetch.
    queryClient.setQueryData(['aiPortfolioHoldings', portfolioId], (old) => (
      old?.portfolio ? { ...old, portfolio: { ...old.portfolio, is_public: newValue } } : old
    ));
    queryClient.invalidateQueries({ queryKey: ['aiPortfolios'] });
    queryClient.invalidateQueries(['aiPortfolioHoldings', portfolioId]);
    queryClient.invalidateQueries(['publicPortfolios']);
  };

  const handleRebalance = async () => {
    const result = await rebalanceMutation.mutateAsync(portfolioId);
    if (result?.aiSummary) setLatestRebalanceSummary(result.aiSummary);
    queryClient.invalidateQueries(['aiPortfolioHoldings', portfolioId]);
    queryClient.invalidateQueries(['aiPortfolioTransactions', portfolioId]);
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this portfolio and all its transactions? This cannot be undone.')) return;
    await deleteMutation.mutateAsync(portfolioId);
    queryClient.invalidateQueries(['aiPortfolios']);
    onBack();
  };

  if (holdingsLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin h-8 w-8 border-3 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!portfolio) {
    return <div className="p-8 text-center text-gray-500">Portfolio not found</div>;
  }

  const plColor = (summary?.total_pl || 0) >= 0 ? 'text-green-600' : 'text-red-600';

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
        {/* Top row: back, name, badges, actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
          <button onClick={onBack} style={{ padding: '6px', background: 'none', border: 'none', cursor: 'pointer', borderRadius: '6px' }} className="hover:bg-gray-100">
            <MuiIcons.ArrowBack style={{ fontSize: 20, color: '#6b7280' }} />
          </button>
          <h1 style={{ fontSize: '1.125rem', fontWeight: 600, color: '#111827', margin: 0 }}>{portfolio.name}</h1>
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STRATEGY_COLORS[getBaseStrategy(portfolio.strategy)] || STRATEGY_COLORS[portfolio.strategy] || 'bg-gray-100'}`}>
            {STRATEGY_LABELS[getBaseStrategy(portfolio.strategy)] || STRATEGY_LABELS[portfolio.strategy] || portfolio.strategy}
          </span>
          {portfolio.status !== 'active' && (
            <span style={{ padding: '2px 8px', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 500, background: '#fef3c7', color: '#92400e' }}>Paused</span>
          )}
          {/* A private portfolio's URL isn't offered for sharing; follows the owner's public toggle. */}
          {!!portfolio.is_public && <ListShareButton path={getListPath(portfolioId)} />}

          {/* Action buttons — owner only */}
          {isOwner ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '2px', marginLeft: 'auto', background: '#f3f4f6', borderRadius: '8px', padding: '4px' }}>
              <button
                onClick={handleRebalance}
                disabled={rebalanceMutation.isPending || portfolio.status !== 'active'}
                style={{ padding: '6px', background: 'none', border: 'none', cursor: rebalanceMutation.isPending || portfolio.status !== 'active' ? 'not-allowed' : 'pointer', borderRadius: '4px', opacity: rebalanceMutation.isPending || portfolio.status !== 'active' ? 0.4 : 1, color: '#2563eb' }}
                className="hover:bg-blue-100"
                data-tooltip-id="ptf-actions" data-tooltip-content="Rebalance now"
              >
                {rebalanceMutation.isPending ? (
                  <div style={{ width: 18, height: 18, border: '2px solid #3b82f6', borderTopColor: 'transparent', borderRadius: '50%' }} className="animate-spin" />
                ) : (
                  <MuiIcons.Refresh style={{ fontSize: 18 }} />
                )}
              </button>
              <button
                onClick={() => setShowEditModal(true)}
                style={{ padding: '6px', background: 'none', border: 'none', cursor: 'pointer', borderRadius: '4px', color: '#4b5563' }}
                className="hover:bg-gray-200"
                data-tooltip-id="ptf-actions" data-tooltip-content="Settings"
              >
                <MuiIcons.Settings style={{ fontSize: 18 }} />
              </button>
              <button
                onClick={handleToggleStatus}
                style={{ padding: '6px', background: 'none', border: 'none', cursor: 'pointer', borderRadius: '4px', color: '#4b5563' }}
                className="hover:bg-gray-200"
                data-tooltip-id="ptf-actions" data-tooltip-content={portfolio.status === 'active' ? 'Pause portfolio' : 'Resume portfolio'}
              >
                {portfolio.status === 'active' ? <MuiIcons.Pause style={{ fontSize: 18 }} /> : <MuiIcons.PlayArrow style={{ fontSize: 18 }} />}
              </button>
              <button
                onClick={handleToggleNotifications}
                style={{ padding: '6px', background: 'none', border: 'none', cursor: 'pointer', borderRadius: '4px', color: portfolio.notifications_enabled !== 0 ? '#2563eb' : '#9ca3af' }}
                className={portfolio.notifications_enabled !== 0 ? 'hover:bg-blue-100' : 'hover:bg-gray-200'}
                data-tooltip-id="ptf-actions" data-tooltip-content={portfolio.notifications_enabled !== 0 ? 'Notifications on' : 'Notifications off'}
              >
                {portfolio.notifications_enabled !== 0
                  ? <MuiIcons.NotificationsActive style={{ fontSize: 18 }} />
                  : <MuiIcons.NotificationsNone style={{ fontSize: 18 }} />}
              </button>
              <button
                onClick={handleTogglePublic}
                style={{ padding: '6px', background: 'none', border: 'none', cursor: 'pointer', borderRadius: '4px', color: portfolio.is_public ? '#2563eb' : '#9ca3af' }}
                className={portfolio.is_public ? 'hover:bg-blue-100' : 'hover:bg-gray-200'}
                data-tooltip-id="ptf-actions" data-tooltip-content={portfolio.is_public ? 'Public (click to make private)' : 'Private (click to share)'}
              >
                {portfolio.is_public
                  ? <MuiIcons.Share style={{ fontSize: 18 }} />
                  : <MuiIcons.LockOutlined style={{ fontSize: 18 }} />}
              </button>
              <div style={{ width: '1px', height: '16px', background: '#d1d5db', margin: '0 2px' }} />
              <button
                onClick={handleDelete}
                style={{ padding: '6px', background: 'none', border: 'none', cursor: 'pointer', borderRadius: '4px', color: '#ef4444' }}
                className="hover:bg-red-100"
                data-tooltip-id="ptf-actions" data-tooltip-content="Delete portfolio"
              >
                <MuiIcons.Delete style={{ fontSize: 18 }} />
              </button>
              <Tooltip id="ptf-actions" place="bottom" offset={5} />
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: 'auto' }}>
              <MuiIcons.Visibility style={{ fontSize: 14, color: '#9ca3af' }} />
              <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Read-only</span>
            </div>
          )}
        </div>

        {/* Strategy description - always visible */}
        {portfolio.strategy_description && (
          <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: '0 0 0.75rem 0', lineHeight: 1.5 }}>
            {portfolio.strategy_description}
          </p>
        )}

        {/* Key metrics */}
        {summary && (
          <>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '2rem' }}>
              <div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '2px' }}>Total Value</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#111827' }}>{formatCurrency(summary.total_value)}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '2px' }}>Total P&L</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 600, color: (summary.total_pl || 0) >= 0 ? '#16a34a' : '#dc2626' }}>
                  {formatPct(summary.total_pl_pct)}
                  <span style={{ fontSize: '0.875rem', fontWeight: 400, color: '#6b7280', marginLeft: '4px' }}>({formatCurrency(summary.total_pl)})</span>
                </div>
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '2px' }}>Today</div>
                <div style={{ fontSize: '1.125rem', fontWeight: 500, color: (summary.today_pl || 0) >= 0 ? '#16a34a' : '#dc2626' }}>
                  {formatPct(summary.today_pl_pct)}
                </div>
              </div>
            </div>

            {/* Expandable details */}
            <div style={{ marginTop: '1rem', paddingTop: '0.75rem', borderTop: '1px solid #f3f4f6' }}>
              <button
                onClick={() => setShowDetails(!showDetails)}
                style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.75rem', color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              >
                <MuiIcons.ExpandMore style={{ fontSize: 16, transition: 'transform 0.2s', transform: showDetails ? 'rotate(180deg)' : 'rotate(0deg)' }} />
                {showDetails ? 'Hide details' : 'Show details'}
              </button>

              {showDetails && (() => {
                const c = typeof portfolio.constraints === 'string' ? JSON.parse(portfolio.constraints || '{}') : (portfolio.constraints || {});
                const drawdownMatch = portfolio.risk_tolerance?.match(/^max_drawdown_(\d+)pct$/);
                const drawdown = c.max_drawdown_pct || (drawdownMatch ? drawdownMatch[1] : null);
                // Map legacy entry_mode to trading_style
                let tradingStyle = c.trading_style || c.entry_mode;
                if (tradingStyle === 'opportunistic') tradingStyle = 'active';
                else if (tradingStyle === 'confirmed') tradingStyle = 'balanced';
                const styleLabel = { active: 'Active', balanced: 'Balanced', patient: 'Patient' }[tradingStyle] || 'Balanced';
                const taxLabel = { tax_agnostic: null, short_term_friendly: 'Short-Term', long_term_optimized: 'Long-Term' }[c.tax_treatment];
                return (
                  <>
                    <div style={{ marginTop: '0.75rem', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem 1.5rem' }}>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Cash</div>
                        <div style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>{formatCurrency(summary.cash_balance)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Invested</div>
                        <div style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>{formatCurrency(summary.invested_value)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Inception</div>
                        <div style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>{portfolio.created_at ? new Date(portfolio.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Trading Style</div>
                        <div style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>{styleLabel}</div>
                      </div>
                      {drawdown && (
                        <div>
                          <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Max Drawdown</div>
                          <div style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>{drawdown}%</div>
                        </div>
                      )}
                      <div>
                        <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Stop / Profit</div>
                        <div style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                          {c.stop_loss_pct ? `${c.stop_loss_pct}%` : '—'} / {c.take_profit_pct ? `${c.take_profit_pct}%` : 'thesis'}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Position Limits</div>
                        <div style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>{c.max_position_pct || 15}% / {c.max_positions || 10} max</div>
                      </div>
                      {taxLabel && (
                        <div>
                          <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Tax Mode</div>
                          <div style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>{taxLabel}</div>
                        </div>
                      )}
                    </div>
                    {portfolio.accrued_learnings && (
                      <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #f3f4f6' }}>
                        <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <MuiIcons.School style={{ fontSize: 14 }} />
                          Accrued Learnings
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-line' }}>{portfolio.accrued_learnings}</div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-3 border-b border-gray-200 pb-2">
        {[
          { key: 'holdings', label: 'Holdings', icon: 'AccountBalance' },
          { key: 'transactions', label: 'Trades', icon: 'SwapHoriz' },
          { key: 'performance', label: 'Performance', icon: 'ShowChart' },
          { key: 'journal', label: 'AI Journal', icon: 'Psychology' },
        ].map(tab => {
          const Icon = MuiIcons[tab.icon];
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors flex items-center gap-1.5
                ${isActive ? 'bg-blue-500 text-white' : 'text-gray-500 hover:bg-gray-100'}`}
            >
              <Icon style={{ fontSize: 16 }} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Holdings Tab */}
      {activeTab === 'holdings' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {holdings.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No positions yet. AI allocation may still be processing.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Symbol</th>
                  <th className="text-right px-4 py-2 font-medium text-gray-600">Shares</th>
                  <th className="text-right px-4 py-2 font-medium text-gray-600">Avg Cost</th>
                  <th className="text-right px-4 py-2 font-medium text-gray-600">Price</th>
                  <th className="text-right px-4 py-2 font-medium text-gray-600">Value</th>
                  <th className="text-right px-4 py-2 font-medium text-gray-600">P&L $</th>
                  <th className="text-right px-4 py-2 font-medium text-gray-600">P&L %</th>
                  <th className="text-right px-4 py-2 font-medium text-gray-600">Weight</th>
                  <th className="text-right px-4 py-2 font-medium text-gray-600">Holding</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Sector</th>
                </tr>
              </thead>
              <tbody>
                {holdings.map(h => {
                  const isExpanded = expandedRow === h.symbol;
                  const stockObj = stockDataMap[h.symbol];
                  return (
                    <React.Fragment key={h.symbol}>
                      <tr
                        className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                        onClick={() => setExpandedRow(isExpanded ? null : h.symbol)}
                      >
                        <td className="px-4 py-2 font-medium">
                          <span className="flex items-center gap-1">
                            <MuiIcons.ExpandMore
                              fontSize="small"
                              className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                            />
                            {h.symbol}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right">{h.shares}</td>
                        <td className="px-4 py-2 text-right">{formatCurrency(h.avgCost)}</td>
                        <td className="px-4 py-2 text-right">{formatCurrency(h.currentPrice)}</td>
                        <td className="px-4 py-2 text-right">{formatCurrency(h.marketValue)}</td>
                        <td className={`px-4 py-2 text-right ${h.plDollar >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(h.plDollar)}
                        </td>
                        <td className={`px-4 py-2 text-right ${h.plPercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatPct(h.plPercent)}
                        </td>
                        <td className="px-4 py-2 text-right">{h.weight.toFixed(1)}%</td>
                        <td className="px-4 py-2 text-right">{h.entryDate ? `${tradingDaysSince(h.entryDate)}d` : '—'}</td>
                        <td className="px-4 py-2 text-gray-500 text-xs">{h.industry}</td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={10} className="p-0">
                            <div className="bg-gray-50 border-b border-gray-200 p-4">
                              {/* The panel renders the stock's own data with or
                                  without an AI analysis (issue #445). */}
                              {stockObj ? (
                                <AiAnalysisPanel aiAnalysis={stockObj.ai_analysis} stock={stockObj} />
                              ) : (
                                <div className="text-sm text-gray-500 text-center py-4">
                                  Loading stock data...
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Transactions Tab */}
      {activeTab === 'transactions' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {txLoading ? (
            <div className="p-8 text-center text-gray-400">Loading transactions...</div>
          ) : !transactions || transactions.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No transactions yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Date</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Symbol</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Action</th>
                  <th className="text-right px-4 py-2 font-medium text-gray-600">Shares</th>
                  <th className="text-right px-4 py-2 font-medium text-gray-600">Price</th>
                  <th className="text-right px-4 py-2 font-medium text-gray-600">Total</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Source</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Reasoning</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map(tx => (
                  <tr key={tx.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-2">{tx.date}</td>
                    <td className="px-4 py-2 font-medium">{tx.symbol}</td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium uppercase
                        ${tx.action === 'buy' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {tx.action}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right">{tx.shares}</td>
                    <td className="px-4 py-2 text-right">{formatCurrency(tx.price)}</td>
                    <td className="px-4 py-2 text-right">{formatCurrency(tx.total)}</td>
                    <td className="px-4 py-2 text-xs text-gray-500">
                      {tx.source === 'ai_initial' ? 'Initial' : tx.source === 'ai_rebalance' ? 'Rebalance' : tx.source}
                    </td>
                    <td className="px-4 py-2">
                      {tx.reasoning ? (
                        <button
                          onClick={() => setExpandedTx(expandedTx === tx.id ? null : tx.id)}
                          className="text-xs text-blue-500 hover:text-blue-700"
                        >
                          {expandedTx === tx.id ? 'Hide' : 'Show'}
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                      {expandedTx === tx.id && (
                        <div className="mt-1 text-xs text-gray-600 bg-gray-50 p-2 rounded">{tx.reasoning}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Performance Tab */}
      {activeTab === 'performance' && (
        <PerformanceChart portfolioId={portfolio.id} />
      )}

      {/* AI Journal Tab */}
      {activeTab === 'journal' && (
        <AiDecisionsPanel
          summary={latestRebalanceSummary || portfolio?.last_ai_summary}
          memoryEntries={memoryEntries}
          transactions={transactions}
        />
      )}

      {/* Edit Settings Modal — owner only */}
      {isOwner && (
        <AIPortfolioCreateModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {}}
          loggedInUser={loggedInUser}
          editPortfolio={portfolio}
        />
      )}
    </div>
  );
};

export default PortfolioView;
